import React from 'react';
import { Alert, Box, Link, Stack, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { OpenInNew, UploadFile } from '@mui/icons-material';

export type AttachmentReplacementStatus = 'idle' | 'uploading' | 'failed';

interface OrderAttachmentsProps {
  customerUploadedPlans: URL[];
  canReplaceCustomerUploadedPlans: boolean;
  replacementStatus: AttachmentReplacementStatus;
  onOpenCustomerUploadedPlans: () => void;
  onReplaceCustomerUploadedPlans: (file: File) => Promise<void> | void;
}

const acceptedAttachmentExtensions = import.meta.env.VITE_APP_ATTACHMENT_ALLOWED_EXTENSIONS ?? '.jpg,.jpeg,.png,.pdf';

const OrderAttachments: React.FC<OrderAttachmentsProps> = ({
  customerUploadedPlans,
  canReplaceCustomerUploadedPlans,
  replacementStatus,
  onOpenCustomerUploadedPlans,
  onReplaceCustomerUploadedPlans
}) => {
  const isUploading = replacementStatus === 'uploading';
  const hasCustomerUploadedPlans = customerUploadedPlans.length > 0;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || isUploading) {
      return;
    }

    await onReplaceCustomerUploadedPlans(file);
  };

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          disabled={!hasCustomerUploadedPlans}
          onClick={hasCustomerUploadedPlans ? onOpenCustomerUploadedPlans : undefined}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: '#008E9C',
            fontWeight: 500,
            textAlign: 'left',
            cursor: hasCustomerUploadedPlans ? 'pointer' : 'default'
          }}
        >
          <OpenInNew sx={{ fontSize: 16 }} />
          Customer Uploaded Plans
        </Link>

        {canReplaceCustomerUploadedPlans && (
          <LoadingButton
            component="label"
            size="small"
            variant="outlined"
            loading={isUploading}
            disabled={isUploading}
            startIcon={<UploadFile />}
            sx={{ textTransform: 'none' }}
          >
            Upload / Replace
            <input
              data-testid="customer-uploaded-plans-input"
              hidden
              type="file"
              accept={acceptedAttachmentExtensions}
              onChange={handleFileChange}
            />
          </LoadingButton>
        )}
      </Box>

      {!hasCustomerUploadedPlans && (
        <Typography variant="caption" color="text.secondary">
          No file available
        </Typography>
      )}

      {replacementStatus === 'failed' && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          Customer uploaded plans could not be replaced.
        </Alert>
      )}
    </Stack>
  );
};

export default OrderAttachments;
